/**
 * ConfirmDeleteDialog
 * Reusable confirmation dialog for destructive actions.
 * Requires the user to type a confirmation phrase (e.g. email or tenant name)
 * before proceeding. Supports archive (soft) and permanent delete modes.
 */

import { useState, type FormEvent } from 'react'
import { AlertTriangle, X, Trash2, Archive } from 'lucide-react'

export type DeleteMode = 'archive' | 'delete'

interface ConfirmDeleteDialogProps {
  /** Whether the dialog is visible. */
  isOpen: boolean
  /** Called when the user cancels or closes the dialog. */
  onClose: () => void
  /** Called when the user confirms the action. */
  onConfirm: (mode: DeleteMode, reason: string) => Promise<void> | void
  /** Display name of the entity being deleted (shown in the header). */
  entityName: string
  /** The type of entity, e.g. "tenant", "user", "customer". */
  entityType: string
  /** The exact string the user must type to confirm. */
  confirmationPhrase: string
  /** Label for what the confirmation input expects (e.g. "email address", "tenant name"). */
  confirmationLabel: string
  /** Whether to show the archive option. Default true. */
  showArchiveOption?: boolean
  /** Warning text shown below the header. */
  warningText?: string
}

export default function ConfirmDeleteDialog({
  isOpen,
  onClose,
  onConfirm,
  entityName,
  entityType,
  confirmationPhrase,
  confirmationLabel,
  showArchiveOption = true,
  warningText,
}: ConfirmDeleteDialogProps) {
  const [mode, setMode] = useState<DeleteMode>(showArchiveOption ? 'archive' : 'delete')
  const [typed, setTyped] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen) return null

  const isMatch = typed.trim().toLowerCase() === confirmationPhrase.trim().toLowerCase()

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!isMatch || reason.trim().length < 3) return
    setBusy(true)
    setError('')
    try {
      await onConfirm(mode, reason.trim())
      setTyped('')
      setReason('')
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Could not ${mode} ${entityType}`)
    } finally {
      setBusy(false)
    }
  }

  const handleClose = () => {
    if (busy) return
    setTyped('')
    setReason('')
    setError('')
    setMode(showArchiveOption ? 'archive' : 'delete')
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Confirm ${mode} ${entityType}`}
    >
      <button
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        disabled={busy}
      />
      <section className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl dark:bg-surface-dark">
        {/* Header */}
        <header className="mb-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {mode === 'archive' ? 'Archive' : 'Move to Recycle Bin'} {entityType}
              </h2>
              <p className="text-sm text-text-secondary">
                {entityName}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleClose}
            disabled={busy}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Warning */}
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {warningText ??
            (mode === 'archive'
              ? `Archiving will disable access to this ${entityType} but preserve all data for audit and recovery.`
              : `This ${entityType} will be inaccessible but recoverable from the Recycle Bin for 30 days.`)}
        </div>

        {/* Mode selector */}
        {showArchiveOption && (
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setMode('archive')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
                mode === 'archive'
                  ? 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                  : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
              }`}
            >
              <Archive className="h-4 w-4" />
              Archive (soft delete)
            </button>
            <button
              type="button"
              onClick={() => setMode('delete')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
                mode === 'delete'
                  ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-400'
                  : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              Move to Recycle Bin
            </button>
          </div>
        )}

        {/* Confirmation form */}
        <form onSubmit={(event) => void handleSubmit(event)}>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            Type the {confirmationLabel} to confirm:{' '}
            <code className="rounded bg-surface-overlay px-1.5 py-0.5 text-xs font-semibold text-text-primary">
              {confirmationPhrase}
            </code>
          </label>
          <input
            type="text"
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmationPhrase}
            className="input input-bordered mt-1 w-full"
            disabled={busy}
          />
          <label className="mt-4 mb-1 block text-sm font-medium text-text-primary">
            Reason for deletion
          </label>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="input input-bordered min-h-20 w-full"
            placeholder="Required for the audit trail"
            disabled={busy}
          />

          {error && (
            <div
              role="alert"
              className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
            >
              {error}
            </div>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="btn"
              onClick={handleClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isMatch || reason.trim().length < 3 || busy}
              className={`btn ${mode === 'delete' ? 'btn-error' : 'btn-warning'}`}
            >
              {busy
                ? mode === 'archive'
                  ? 'Archiving…'
                  : 'Moving…'
                : mode === 'archive'
                  ? `Archive ${entityType}`
                  : `Move ${entityType}`}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}
